import { useCallback, useEffect, useRef, useState } from 'react';
import useModalContext from '@hooks/context/useModalContext';
import { useUserContext } from '@hooks/context/useUserContext';
import { modalIds } from '@const/modals';

const LAST_ACTIVITY_KEY = 'lastActivityTime';

export const useUserActivity = () => {
    const { onOpen, onClose, changeModalData } = useModalContext();
    const { handleLogout } = useUserContext();
    const [showModal, setShowModal] = useState(false);
    const [timeLeft, setTimeLeft] = useState(30);
    const timerRef = useRef<number | null>(null);
    const countdownRef = useRef<number | null>(null);
    // BUG D fix: ref instead of state so clearInterval inside checkLastActivity
    // always targets the running interval (state is stale inside the interval closure)
    const logoutIntervalRef = useRef<number | null>(null);

    const resetTimer = useCallback((): void => {
        const now = new Date().getTime();
        localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());

        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        setTimeLeft(30);
        countdownRef.current = window.setInterval(() => {
            setTimeLeft((prev) => prev - 1);
        }, 1000);

        // Only start the timer if the modal is not shown
        if (!showModal) {
            timerRef.current = window.setTimeout(() => {
                setShowModal(true);
            }, 870000); // 870000ms = 14 minutes 30 seconds
            if (countdownRef.current) {
                clearInterval(countdownRef.current);
            }
        }
    }, [showModal]);

    const handleUserActivity = () => {
        setShowModal(false); // Hide modal if it's showing
        resetTimer();
    };

    // BUG B fix: keep a ref to the latest handleUserActivity so the onOpen
    // closure (created once on showModal→true) calls the current version when the
    // cancel button is clicked up to 30 seconds later
    const handleUserActivityRef = useRef(handleUserActivity);
    handleUserActivityRef.current = handleUserActivity;

    const checkLastActivity = () => {
        const lastActivityTime = localStorage.getItem(LAST_ACTIVITY_KEY);
        if (lastActivityTime) {
            const now = new Date().getTime();
            const elapsed = now - parseInt(lastActivityTime, 10);

            if (elapsed > 900000) {
                // 900000ms = 15 minutes
                if (logoutIntervalRef.current) {
                    clearInterval(logoutIntervalRef.current); // BUG D fix: ref is always current
                }
                onClose();
                handleLogout(true, { logged_out: true });
            }
        }
    };

    useEffect(() => {
        const interval = window.setInterval(checkLastActivity, 1000);
        logoutIntervalRef.current = interval; // BUG D fix: store in ref, not state

        return () => {
            clearInterval(interval);
        };
    }, []);

    // BUG C fix: single effect owns all listener add/remove — no duplicate registration
    useEffect(() => {
        window.addEventListener('mousedown', handleUserActivity);
        window.addEventListener('keypress', handleUserActivity);
        window.addEventListener('scroll', handleUserActivity);
        document.addEventListener('visibilitychange', checkLastActivity);
        resetTimer();
        return () => {
            window.removeEventListener('mousedown', handleUserActivity);
            window.removeEventListener('keypress', handleUserActivity);
            window.removeEventListener('scroll', handleUserActivity);
            document.removeEventListener('visibilitychange', checkLastActivity);
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
            if (countdownRef.current) {
                clearInterval(countdownRef.current);
            }
        };
    }, [resetTimer]);

    // BUG B fix: deps reduced from [showModal, timeLeft] to [showModal] so onOpen
    // fires exactly once when showModal transitions to true, not on every countdown tick.
    // The cancel handler reads handleUserActivityRef.current to avoid a stale closure.
    useEffect(() => {
        if (showModal) {
            onOpen({
                modalId: modalIds.USER_ACTIVITY_MODAL,
                onSubmit: () => ({
                    logout: () => {
                        handleLogout();
                        setTimeout(() => {
                            onClose();
                        }, 0);
                    },
                    cancel: () => {
                        if (countdownRef.current) {
                            clearInterval(countdownRef.current);
                        }
                        if (timerRef.current) {
                            clearInterval(timerRef.current);
                        }
                        setTimeLeft(30);
                        handleUserActivityRef.current(); // always the latest handleUserActivity
                        onClose();
                    }
                })
            });
        }
    }, [showModal]);

    useEffect(() => {
        if (timeLeft < 30) {
            changeModalData({ timer: timeLeft });
        }
        if (timeLeft <= 0) {
            // BUG A fix: stop the countdown before logging out so handleLogout
            // fires exactly once instead of on every subsequent decrement tick
            if (countdownRef.current) {
                clearInterval(countdownRef.current);
                countdownRef.current = null;
            }
            handleLogout(true, { logged_out: true });
            setTimeout(() => {
                onClose();
            }, 0);
        }
    }, [timeLeft]);
};
